import type { AsyncInvokeAgentProgressSnapshot } from '@einwert/valuation-api-client'
import { Typography } from 'ComponentLibrary/src/piazza'
import { tokens } from 'ComponentLibrary'
import { PdfExtractionSteps } from 'ComponentLibrary/src/common/valuation-import/PdfExtractionSteps'
import { PendingImportMessage } from 'ComponentLibrary/src/common/valuation-import/PendingImportMessage'
import { ElapsedImportTime } from 'ComponentLibrary/src/common/valuation-import/ElapsedImportTime'
import { tenantImportCopy, type TenantImportLanguage } from './copy'

const FINISHED_STATUSES = ['completed', 'skipped', 'not_found', 'failed']

export function TenantImportProgress({
    progress,
    isFollowUp,
    language = 'de',
}: {
    progress?: AsyncInvokeAgentProgressSnapshot
    isFollowUp: boolean
    language?: TenantImportLanguage
}) {
    const copy = tenantImportCopy(language)
    const labels: Record<string, string> = {
        source: isFollowUp ? copy.applying : copy.preparing,
        layout: isFollowUp ? copy.updating : copy.reading,
        tenants: isFollowUp ? copy.checking : copy.extracting,
        prepare: copy.preparingImport,
    }
    const progressSteps = progress?.steps.map((step) => ({ label: labels[step.id] ?? step.label[language], status: step.status }))
    const awaitingResult = !!progress?.steps.length && progress.steps.every((step) => FINISHED_STATUSES.includes(step.status))
    const stepIndices = (statuses: string[]) =>
        progressSteps?.flatMap((step, index) => (statuses.includes(step.status) ? [index] : [])) ?? []
    return (
        <>
            <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                {isFollowUp ? copy.followUpTitle : copy.progressTitle}
            </Typography>
            <Typography variant="textS" color={tokens.ColorTextDefault}>
                {isFollowUp ? copy.followUpDescription : copy.progressDescription}
            </Typography>
            <PdfExtractionSteps
                sectionLabels={progressSteps?.map((step) => step.label) ?? Object.values(labels)}
                activeSectionIndexes={progress ? stepIndices(['running', 'retrying']) : [0]}
                completedSectionIndexes={stepIndices(['completed', 'skipped', 'not_found'])}
                failedSectionIndexes={stepIndices(['failed'])}
                retryingSectionIndexes={stepIndices(['retrying'])}
                sectionTimings={{}}
                headerStatus={<ElapsedImportTime />}
                copy={{
                    title: isFollowUp ? copy.followUpTitle : copy.progressTitle,
                    description: isFollowUp ? copy.followUpDescription : copy.progressDescription,
                    retrying: copy.retrying,
                    formatElapsedSeconds: (seconds) => `${seconds} s`,
                    failedSectionAriaLabel: copy.failedSection,
                }}
            />
            {awaitingResult && <PendingImportMessage message={copy.preparingPreview} />}
        </>
    )
}
